/** Device and capability detection for voice capture. */

export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    || ('ontouchstart' in window && window.innerWidth <= 1024)
  );
};

export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
};

export const isStandalonePwa = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

export const supportsWebSpeechRecognition = (): boolean => {
  if (typeof window === 'undefined') return false;
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

export const supportsMediaRecorderCapture = (): boolean => {
  if (typeof window === 'undefined') return false;
  return typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
};

const SERVER_TRANSCRIPTION_UNAVAILABLE_KEY = 'voiceServerTranscriptionUnavailable';

/**
 * Use server-side MediaRecorder transcription when Web Speech is unavailable
 * or unreliable (iOS Safari). Android Chrome Web Speech is reliable and avoids
 * a server round-trip; iOS needs Whisper because browser speech is flaky.
 */
export const shouldUseMediaRecorderFallback = (): boolean => {
  if (!supportsMediaRecorderCapture()) return false;
  if (!supportsWebSpeechRecognition()) return true;
  if (isIOS()) return true;
  return false;
};

/** After server transcription fails on iOS, prefer browser speech on the next tap. */
export const shouldPreferBrowserSpeechOnMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(SERVER_TRANSCRIPTION_UNAVAILABLE_KEY) === '1';
  } catch {
    return false;
  }
};

export const markServerTranscriptionUnavailable = (): void => {
  try {
    sessionStorage.setItem(SERVER_TRANSCRIPTION_UNAVAILABLE_KEY, '1');
  } catch {
    /* ignore quota / private mode */
  }
};

export const clearServerTranscriptionUnavailable = (): void => {
  try {
    sessionStorage.removeItem(SERVER_TRANSCRIPTION_UNAVAILABLE_KEY);
  } catch {
    /* ignore */
  }
};

export const getPreferredMediaRecorderMimeType = (): string | undefined => {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'];
  return types.find((type) => MediaRecorder.isTypeSupported(type));
};
